import type {
  MapExperienceInteractivityDefinition,
  MapExperienceMetadataV1,
  MapExperiencePipelineStepKind,
  MapExperiencePipelineTriggerKind,
  MapExperienceResolverFailurePolicy,
} from './map-experience';

export type RuntimeFeatureId = string | number;

export interface RuntimeFeatureRef {
  source_id: string;
  source_layer: string;
  feature_id: RuntimeFeatureId;
  properties?: Record<string, unknown> | null;
  geometry?: Record<string, unknown> | null;
}

export interface RuntimeFeatureStateRecord {
  source_id: string;
  source_layer: string;
  feature_id: RuntimeFeatureId;
  state: Record<string, unknown>;
}

export interface RuntimeFeatureStatePatch {
  resolver_id: string;
  status?: 'ok' | 'empty' | 'warning' | null;
  records: RuntimeFeatureStateRecord[];
  warnings?: string[] | null;
}

export interface RuntimeResolverDefinitionLike {
  resolver_id: string;
  resolver_attachment_id?: string | null;
  resolver_type_id?: string | null;
  failure_policy?: MapExperienceResolverFailurePolicy | null;
}

export interface RuntimeSurfaceDefinitionLike {
  surface_id: string;
  source_id?: string | null;
  source_layer?: string | null;
  surface_kind: string;
}

export type RuntimePipelineStepDefinitionLike =
  | {
      step_id: string;
      kind: 'resolve';
      resolver_id: string;
      resolver_attachment_id?: string | null;
      resolver_type_id?: string | null;
      input?: Record<string, unknown> | null;
      input_mappings?: Array<{
        input_key: string;
        binding_kind: 'feature_field' | 'semantic_role' | 'builtin_value' | 'constant' | 'resolver_result';
        binding?: Record<string, unknown> | null;
        required?: boolean | null;
      }> | null;
      failure_policy?: MapExperienceResolverFailurePolicy | null;
    }
  | {
      step_id: string;
      kind: 'target_set';
      input?: string | null;
      selector: Record<string, unknown>;
    }
  | {
      step_id: string;
      kind: 'condition';
      input?: string | null;
      target?: Record<string, unknown> | null;
      logic: Record<string, unknown>;
    };

export type RuntimePipelineResolveStepDefinitionLike = Extract<
  RuntimePipelineStepDefinitionLike,
  { kind: 'resolve' }
>;

export interface RuntimeEffectDefinitionLike {
  effect_id?: string | null;
  kind: string;
  input?: string | Record<string, unknown> | null;
  surface_id?: string | null;
  tool_id?: string | null;
  style?: string | null;
  filter_id?: string | null;
  value?: unknown;
  auto_clear_ms?: number | null;
}

export interface RuntimePipelineDefinitionLike<TTriggerKind extends string = string> {
  pipeline_id: string;
  source_id?: string | null;
  source_layer?: string | null;
  label?: string | null;
  trigger: {
    kind: TTriggerKind;
    source?: string | null;
    geometry_kind?: 'point' | 'line' | 'polygon' | null;
  };
  steps: RuntimePipelineStepDefinitionLike[];
  effects: RuntimeEffectDefinitionLike[];
  stop_propagation?: boolean | null;
}

export interface RuntimeInteractivityDefinitionLike<TTriggerKind extends string = string> {
  resolvers: RuntimeResolverDefinitionLike[];
  surfaces: RuntimeSurfaceDefinitionLike[];
  pipelines: RuntimePipelineDefinitionLike<TTriggerKind>[];
}

export interface MapExperienceResolverExecutionRequest {
  resolver_id: string;
  resolver_attachment_id?: string | null;
  resolver_type_id?: string | null;
  pipeline_id: string;
  step_id: string;
  trigger_kind: MapExperiencePipelineTriggerKind;
  source_id?: string | null;
  source_layer?: string | null;
  feature?: RuntimeFeatureRef | null;
  input?: Record<string, unknown> | null;
}

export interface RuntimeFeatureStateBinding {
  owner_key: string;
  pipeline_id: string;
  effect_id: string | null;
  resolver_id: string;
  records: RuntimeFeatureStateRecord[];
  warnings: string[];
}

export interface MapExperienceRuntimeTrigger<TTriggerKind extends string = MapExperiencePipelineTriggerKind> {
  kind: TTriggerKind;
  feature?: RuntimeFeatureRef | null;
}

export type RuntimePipelineStepExecutionStatus = 'completed' | 'empty' | 'warning' | 'failed' | 'not_implemented';

export interface RuntimePipelineStepExecution {
  step_id: string;
  kind: MapExperiencePipelineStepKind;
  status: RuntimePipelineStepExecutionStatus;
  resolver_id?: string | null;
  warnings: string[];
  error?: string | null;
  patch?: RuntimeFeatureStatePatch | null;
}

export type RuntimePipelineEffectExecutionStatus = 'ready' | 'blocked' | 'not_implemented';

export interface RuntimePipelineEffectExecution<TTriggerKind extends string = string> {
  pipeline_id: string;
  pipeline: RuntimePipelineDefinitionLike<TTriggerKind>;
  effect_id: string | null;
  effect: RuntimeEffectDefinitionLike;
  kind: string;
  status: RuntimePipelineEffectExecutionStatus;
  surface_id: string | null;
  surface: RuntimeSurfaceDefinitionLike | null;
  resolver_id: string | null;
  records: RuntimeFeatureStateRecord[];
  warnings: string[];
}

export interface RuntimePipelineExecution<TTriggerKind extends string = string> {
  pipeline_id: string;
  pipeline: RuntimePipelineDefinitionLike<TTriggerKind>;
  steps: RuntimePipelineStepExecution[];
  effects: RuntimePipelineEffectExecution<TTriggerKind>[];
  halted: boolean;
  stop_propagation: boolean;
  warnings: string[];
}

export interface RunMapExperienceBindingsArgs {
  experience: MapExperienceMetadataV1;
  trigger: MapExperienceRuntimeTrigger;
  executeResolver: (
    request: MapExperienceResolverExecutionRequest,
  ) => Promise<RuntimeFeatureStatePatch>;
}

export interface RunMapExperienceInteractivityPipelinesArgs<TTriggerKind extends string = string> {
  interactivity: RuntimeInteractivityDefinitionLike<TTriggerKind> | null | undefined;
  trigger: MapExperienceRuntimeTrigger<TTriggerKind>;
  executeResolver?: (
    request: MapExperienceResolverExecutionRequest,
  ) => Promise<RuntimeFeatureStatePatch>;
}

type RuntimePipelineResolveResponse = {
  step_id: string;
  resolver_id: string;
  resolver_attachment_id?: string | null;
  patch: RuntimeFeatureStatePatch;
};

type RuntimeStepExecutionResult = {
  responses: RuntimePipelineResolveResponse[];
  steps: RuntimePipelineStepExecution[];
  halted: boolean;
  warnings: string[];
};

const SUPPORTED_RUNTIME_EFFECT_KINDS = new Set<string>([
  'open_surface',
  'close_surface',
  'bind_surface_data',
  'bind_feature_state',
  'highlight_set',
  'clear_highlight_set',
  'fly_to_feature',
  'open_route',
  'open_external_url',
  'copy_field',
  'activate_builtin_tool',
  'clear_drawings',
  'clear_selection',
]);

const isFeatureId = (value: unknown): value is RuntimeFeatureId =>
  (typeof value === 'string' && value.trim().length > 0) || typeof value === 'number';

const normalizeFeatureStateRecord = (value: RuntimeFeatureStateRecord): RuntimeFeatureStateRecord | null => {
  if (
    typeof value?.source_id !== 'string' ||
    !value.source_id.trim() ||
    typeof value?.source_layer !== 'string' ||
    !value.source_layer.trim() ||
    !isFeatureId(value.feature_id) ||
    !value.state ||
    typeof value.state !== 'object' ||
    Array.isArray(value.state)
  ) {
    return null;
  }
  return {
    source_id: value.source_id.trim(),
    source_layer: value.source_layer.trim(),
    feature_id: typeof value.feature_id === 'string' ? value.feature_id.trim() : value.feature_id,
    state: { ...(value.state as Record<string, unknown>) },
  };
};

const normalizeRuntimeFeatureStatePatch = (
  patch: RuntimeFeatureStatePatch,
  fallbackResolverId: string,
): RuntimeFeatureStatePatch => ({
  resolver_id: patch?.resolver_id?.trim?.() || fallbackResolverId,
  status: patch?.status ?? 'ok',
  records: Array.isArray(patch?.records)
    ? patch.records
        .map(normalizeFeatureStateRecord)
        .filter((entry): entry is RuntimeFeatureStateRecord => Boolean(entry))
    : [],
  warnings: Array.isArray(patch?.warnings)
    ? patch.warnings
        .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
        .map(entry => entry.trim())
    : [],
});

const resolveInteractivity = (experience: MapExperienceMetadataV1): MapExperienceInteractivityDefinition | null =>
  experience.interactivity ?? null;

const resolveResolverFailurePolicy = (
  resolver: RuntimeResolverDefinitionLike | null,
  step: RuntimePipelineResolveStepDefinitionLike,
): MapExperienceResolverFailurePolicy => step.failure_policy ?? resolver?.failure_policy ?? 'skip_step';

const resolveBindingResponse = (
  effect: RuntimeEffectDefinitionLike,
  responses: RuntimePipelineResolveResponse[],
): RuntimePipelineResolveResponse | null => {
  if (responses.length === 0) {
    return null;
  }
  const input =
    typeof effect.input === 'string'
      ? effect.input.trim()
      : effect.input &&
          typeof effect.input === 'object' &&
          !Array.isArray(effect.input) &&
          typeof (effect.input as Record<string, unknown>).step_id === 'string'
        ? String((effect.input as Record<string, unknown>).step_id).trim()
        : '';
  if (!input) {
    return responses[responses.length - 1] ?? null;
  }
  return (
    responses.find(
      response =>
        response.step_id === input ||
        response.resolver_id === input ||
        response.resolver_attachment_id === input,
    ) ??
    responses[responses.length - 1] ??
    null
  );
};

const resolveRuntimeResolverLookupKeys = (resolver: RuntimeResolverDefinitionLike): string[] => {
  const keys = [resolver.resolver_attachment_id, resolver.resolver_id].filter(
    (entry): entry is string => typeof entry === 'string' && entry.trim().length > 0,
  );
  return Array.from(new Set(keys));
};

const resolveRuntimeFeatureFieldValue = (
  feature: RuntimeFeatureRef | null | undefined,
  fieldId: string,
): unknown => {
  const normalizedFieldId = fieldId.trim();
  if (!normalizedFieldId) {
    return null;
  }
  if (normalizedFieldId === 'feature_id') {
    return feature?.feature_id ?? null;
  }
  return feature?.properties?.[normalizedFieldId] ?? null;
};

const resolveRuntimeBuiltinValue = (
  valueId: string,
  trigger: MapExperienceRuntimeTrigger<string>,
): unknown => {
  switch (valueId) {
    case 'current_time':
      return new Date().toISOString();
    case 'selected_feature':
    case 'hovered_feature':
    case 'trigger_feature':
      return trigger.feature ?? null;
    default:
      return null;
  }
};

const resolveRuntimeResolverResponseValue = (
  binding: Record<string, unknown> | null | undefined,
  responses: RuntimePipelineResolveResponse[],
): unknown => {
  if (responses.length === 0) {
    return null;
  }
  const stepId = typeof binding?.step_id === 'string' ? binding.step_id.trim() : '';
  const resolverId = typeof binding?.resolver_id === 'string' ? binding.resolver_id.trim() : '';
  const attachmentId = typeof binding?.resolver_attachment_id === 'string' ? binding.resolver_attachment_id.trim() : '';
  const stateKey = typeof binding?.state_key === 'string' ? binding.state_key.trim() : '';
  const response =
    responses.find(
      entry =>
        (stepId && entry.step_id === stepId) ||
        (resolverId && entry.resolver_id === resolverId) ||
        (attachmentId && entry.resolver_attachment_id === attachmentId),
    ) ??
    responses[responses.length - 1] ??
    null;
  if (!response) {
    return null;
  }
  if (!stateKey) {
    return response.patch;
  }
  for (const record of response.patch.records) {
    if (stateKey in record.state) {
      return record.state[stateKey];
    }
  }
  return null;
};

const buildRuntimeResolverInput = (
  step: RuntimePipelineResolveStepDefinitionLike,
  trigger: MapExperienceRuntimeTrigger<string>,
  responses: RuntimePipelineResolveResponse[],
): Record<string, unknown> | null => {
  const baseInput =
    step.input && typeof step.input === 'object' && !Array.isArray(step.input)
      ? { ...step.input }
      : {};
  const mappings = Array.isArray(step.input_mappings) ? step.input_mappings : [];
  if (mappings.length === 0) {
    return Object.keys(baseInput).length > 0 ? baseInput : null;
  }
  const nextInput: Record<string, unknown> = { ...baseInput };
  for (const mapping of mappings) {
    const key = typeof mapping?.input_key === 'string' ? mapping.input_key.trim() : '';
    if (!key) {
      continue;
    }
    let resolvedValue: unknown = null;
    switch (mapping.binding_kind) {
      case 'feature_field':
        resolvedValue =
          typeof mapping.binding?.field === 'string'
            ? resolveRuntimeFeatureFieldValue(trigger.feature, mapping.binding.field)
            : null;
        break;
      case 'semantic_role':
        if (typeof mapping.binding?.field === 'string') {
          resolvedValue = resolveRuntimeFeatureFieldValue(trigger.feature, mapping.binding.field);
        } else if (typeof mapping.binding?.fallback_field === 'string') {
          resolvedValue = resolveRuntimeFeatureFieldValue(trigger.feature, mapping.binding.fallback_field);
        }
        break;
      case 'builtin_value':
        resolvedValue =
          typeof mapping.binding?.value_id === 'string'
            ? resolveRuntimeBuiltinValue(mapping.binding.value_id, trigger)
            : null;
        break;
      case 'constant':
        resolvedValue = Object.prototype.hasOwnProperty.call(mapping.binding ?? {}, 'value')
          ? mapping.binding?.value
          : null;
        break;
      case 'resolver_result':
        resolvedValue = resolveRuntimeResolverResponseValue(mapping.binding, responses);
        break;
      default:
        resolvedValue = null;
    }
    nextInput[key] = resolvedValue;
  }
  return Object.keys(nextInput).length > 0 ? nextInput : null;
};

const pipelineMatchesTrigger = <TTriggerKind extends string>(
  pipeline: RuntimePipelineDefinitionLike<TTriggerKind>,
  trigger: MapExperienceRuntimeTrigger<TTriggerKind>,
): boolean => {
  if (pipeline.trigger.kind !== trigger.kind) {
    return false;
  }
  if (!pipeline.source_id || !pipeline.source_layer) {
    return false;
  }
  if (!trigger.feature) {
    return true;
  }
  return pipeline.source_id === trigger.feature.source_id && pipeline.source_layer === trigger.feature.source_layer;
};

const buildBindingOwnerKey = (
  pipeline: RuntimePipelineDefinitionLike<string>,
  effect: RuntimeEffectDefinitionLike,
  index: number,
) => `${pipeline.pipeline_id}::${effect.effect_id?.trim() || `bind_feature_state:${index}`}`;

const buildResolverFailureResult = (
  step: RuntimePipelineResolveStepDefinitionLike,
  policy: MapExperienceResolverFailurePolicy,
  message: string,
): { step: RuntimePipelineStepExecution; response: RuntimePipelineResolveResponse | null; halt: boolean } => {
  if (policy === 'show_runtime_warning') {
    const patch: RuntimeFeatureStatePatch = {
      resolver_id: step.resolver_id,
      status: 'warning',
      records: [],
      warnings: [message],
    };
    return {
      step: {
        step_id: step.step_id,
        kind: step.kind,
        status: 'warning',
        resolver_id: step.resolver_id,
        warnings: [message],
        patch,
      },
      response: {
        step_id: step.step_id,
        resolver_id: step.resolver_id,
        resolver_attachment_id: step.resolver_attachment_id ?? null,
        patch,
      },
      halt: false,
    };
  }
  if (policy === 'continue_with_empty_result') {
    const patch: RuntimeFeatureStatePatch = {
      resolver_id: step.resolver_id,
      status: 'empty',
      records: [],
      warnings: [],
    };
    return {
      step: {
        step_id: step.step_id,
        kind: step.kind,
        status: 'empty',
        resolver_id: step.resolver_id,
        warnings: [],
        patch,
      },
      response: {
        step_id: step.step_id,
        resolver_id: step.resolver_id,
        resolver_attachment_id: step.resolver_attachment_id ?? null,
        patch,
      },
      halt: false,
    };
  }
  return {
    step: {
      step_id: step.step_id,
      kind: step.kind,
      status: 'failed',
      resolver_id: step.resolver_id,
      warnings: [message],
      error: message,
      patch: null,
    },
    response: null,
    halt: policy === 'stop_pipeline',
  };
};

const executeRuntimeSteps = async <TTriggerKind extends string>(
  pipeline: RuntimePipelineDefinitionLike<TTriggerKind>,
  trigger: MapExperienceRuntimeTrigger<TTriggerKind>,
  resolversById: Map<string, RuntimeResolverDefinitionLike>,
  executeResolver?: (
    request: MapExperienceResolverExecutionRequest,
  ) => Promise<RuntimeFeatureStatePatch>,
): Promise<RuntimeStepExecutionResult> => {
  const responses: RuntimePipelineResolveResponse[] = [];
  const steps: RuntimePipelineStepExecution[] = [];
  const warnings: string[] = [];
  let halted = false;

  for (const step of pipeline.steps) {
    if (step.kind !== 'resolve') {
      const message =
        step.kind === 'target_set'
          ? 'Target-set steps are not implemented in this runtime yet.'
          : 'Condition steps are not implemented in this runtime yet.';
      steps.push({
        step_id: step.step_id,
        kind: step.kind,
        status: 'not_implemented',
        warnings: [message],
        error: message,
        patch: null,
      });
      warnings.push(message);
      halted = true;
      break;
    }

    const resolver =
      (step.resolver_attachment_id ? resolversById.get(step.resolver_attachment_id) : null) ??
      resolversById.get(step.resolver_id) ??
      null;
    const failurePolicy = resolveResolverFailurePolicy(resolver, step);

    if (!executeResolver) {
      const failure = buildResolverFailureResult(
        step,
        failurePolicy,
        `Resolver execution is not available for step "${step.step_id}".`,
      );
      steps.push(failure.step);
      if (failure.response) {
        responses.push(failure.response);
      }
      warnings.push(...failure.step.warnings);
      if (failure.halt) {
        halted = true;
        break;
      }
      continue;
    }

    try {
      const patch = normalizeRuntimeFeatureStatePatch(
        await executeResolver({
          resolver_id: step.resolver_id,
          resolver_attachment_id: step.resolver_attachment_id ?? null,
          resolver_type_id: step.resolver_type_id ?? resolver?.resolver_type_id ?? null,
          pipeline_id: pipeline.pipeline_id,
          step_id: step.step_id,
          trigger_kind: trigger.kind as MapExperiencePipelineTriggerKind,
          source_id: pipeline.source_id ?? trigger.feature?.source_id ?? null,
          source_layer: pipeline.source_layer ?? trigger.feature?.source_layer ?? null,
          feature: trigger.feature ?? null,
          input: buildRuntimeResolverInput(step, trigger, responses),
        }),
        step.resolver_id,
      );
      responses.push({
        step_id: step.step_id,
        resolver_id: step.resolver_id,
        resolver_attachment_id: step.resolver_attachment_id ?? null,
        patch,
      });
      steps.push({
        step_id: step.step_id,
        kind: step.kind,
        status: patch.status === 'warning' ? 'warning' : patch.status === 'empty' ? 'empty' : 'completed',
        resolver_id: step.resolver_id,
        warnings: patch.warnings ?? [],
        patch,
      });
      warnings.push(...(patch.warnings ?? []));
    } catch (error) {
      const failure = buildResolverFailureResult(
        step,
        failurePolicy,
        error instanceof Error ? error.message : 'Resolver execution failed.',
      );
      steps.push(failure.step);
      if (failure.response) {
        responses.push(failure.response);
      }
      warnings.push(...failure.step.warnings);
      if (failure.halt) {
        halted = true;
        break;
      }
    }
  }

  return {
    responses,
    steps,
    halted,
    warnings,
  };
};

const buildBlockedEffectExecution = <TTriggerKind extends string>(
  pipeline: RuntimePipelineDefinitionLike<TTriggerKind>,
  effect: RuntimeEffectDefinitionLike,
  surface: RuntimeSurfaceDefinitionLike | null,
  warnings: string[],
): RuntimePipelineEffectExecution<TTriggerKind> => ({
  pipeline_id: pipeline.pipeline_id,
  pipeline,
  effect_id: effect.effect_id?.trim() || null,
  effect,
  kind: effect.kind,
  status: 'blocked',
  surface_id: effect.surface_id?.trim() || null,
  surface,
  resolver_id: null,
  records: [],
  warnings,
});

export async function runMapExperienceInteractivityPipelines<TTriggerKind extends string>(
  args: RunMapExperienceInteractivityPipelinesArgs<TTriggerKind>,
): Promise<RuntimePipelineExecution<TTriggerKind>[]> {
  const interactivity = args.interactivity;
  if (!interactivity || interactivity.pipelines.length === 0) {
    return [];
  }

  const resolversById = new Map(
    interactivity.resolvers.flatMap(resolver =>
      resolveRuntimeResolverLookupKeys(resolver).map(key => [key, resolver] as const),
    ),
  );
  const surfacesById = new Map(interactivity.surfaces.map(surface => [surface.surface_id, surface]));
  const executions: RuntimePipelineExecution<TTriggerKind>[] = [];

  for (const pipeline of interactivity.pipelines) {
    if (!pipelineMatchesTrigger(pipeline, args.trigger)) {
      continue;
    }

    const stepResult = await executeRuntimeSteps(
      pipeline,
      args.trigger,
      resolversById,
      args.executeResolver,
    );

    const effects = stepResult.halted
      ? pipeline.effects.map(effect =>
          buildBlockedEffectExecution(
            pipeline,
            effect,
            (effect.surface_id ? surfacesById.get(effect.surface_id) : null) ?? null,
            stepResult.warnings,
          ),
        )
      : pipeline.effects.map(effect => {
          const response = resolveBindingResponse(effect, stepResult.responses);
          const ready = SUPPORTED_RUNTIME_EFFECT_KINDS.has(effect.kind);
          const warnings = [...stepResult.warnings];
          if (!ready) {
            warnings.push(`Effect kind "${effect.kind}" is not implemented in this runtime yet.`);
          }
          return {
            pipeline_id: pipeline.pipeline_id,
            pipeline,
            effect_id: effect.effect_id?.trim() || null,
            effect,
            kind: effect.kind,
            status: ready ? 'ready' : 'not_implemented',
            surface_id: effect.surface_id?.trim() || null,
            surface: (effect.surface_id ? surfacesById.get(effect.surface_id) : null) ?? null,
            resolver_id: response?.resolver_id ?? null,
            records: response?.patch.records ?? [],
            warnings,
          } satisfies RuntimePipelineEffectExecution<TTriggerKind>;
        });

    executions.push({
      pipeline_id: pipeline.pipeline_id,
      pipeline,
      steps: stepResult.steps,
      effects,
      halted: stepResult.halted,
      stop_propagation: pipeline.stop_propagation === true,
      warnings: stepResult.warnings,
    });

    if (pipeline.stop_propagation) {
      break;
    }
  }

  return executions;
}

export async function runMapExperienceFeatureStateBindings(
  args: RunMapExperienceBindingsArgs,
): Promise<RuntimeFeatureStateBinding[]> {
  const interactivity = resolveInteractivity(args.experience);
  if (!interactivity || interactivity.pipelines.length === 0) {
    return [];
  }

  const executions = await runMapExperienceInteractivityPipelines({
    interactivity,
    trigger: args.trigger,
    executeResolver: args.executeResolver,
  });

  const bindings: RuntimeFeatureStateBinding[] = [];
  for (const execution of executions) {
    execution.effects
      .filter(effect => effect.kind === 'bind_feature_state' && effect.status === 'ready')
      .forEach((effect, index) => {
        bindings.push({
          owner_key: buildBindingOwnerKey(execution.pipeline, effect.effect, index),
          pipeline_id: execution.pipeline_id,
          effect_id: effect.effect_id,
          resolver_id: effect.resolver_id ?? '',
          records: effect.records,
          warnings: effect.warnings,
        });
      });
  }

  return bindings;
}
